import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded';

import graph from './graph';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useLazyQuery, useMutation } from '@apollo/client';
import { isEmptyObject } from 'helpers/formatObject';
import { NewDialog, NewDialogActions, NewDialogContent, NewDialogTitle } from 'components';
import { FormattedMessage } from 'react-intl';
import {
  Avatar,
  Badge,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Fade,
  Stack,
  Typography,
} from '@mui/material';

const colors = {
  1: {
    color: 'warning.dark',
    bgcolor: 'warning.lighter',
  },
  2: {
    color: 'success.dark',
    bgcolor: 'success.lighter',
  },
  3: {
    color: 'info.dark',
    bgcolor: 'info.lighter',
  },
  4: {
    color: 'error.dark',
    bgcolor: 'error.lighter',
  },
};

export default function CheckShop({ ids, title, refetch, warning = false }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState([]);
  const { userToken } = useSelector((state) => state.auth);
  const [fetchModel, { loading }] = useLazyQuery(graph.get.query);
  const [request, { loading: requesting }] = useMutation(graph.request.query);

  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);

  const getModel = async () => {
    try {
      const { data } = await fetchModel({
        context: {
          serviceName: graph.get.serviceName,
          headers: {
            authorization: `Bearer ${userToken}`,
          },
        },
        variables: {
          ids,
          // for_admin: 1,
        },
      });
      if (!isEmptyObject(data)) {
        const res = data[graph.get.name];
        if (res) {
          setResult(res);
        }
      }
    } catch (error) {}
  };

  const onRequest = async () => {
    try {
      const { data, errors } = await request({
        context: {
          serviceName: graph.request.serviceName,
          headers: {
            authorization: `Bearer ${userToken}`,
          },
        },
        variables: {
          shop_id: ids,
          status: 'TODO',
          type: 'READY_SHOP',
        },
      });
      if (!errors) {
        refetch();
        onClose();
        if (!isEmptyObject(data)) {
          data[graph.request.name]?.messages.map((message) => toast.success(String(message)));
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    open && getModel();
  }, [open]);

  return (
    <>
      <Tooltip title={<FormattedMessage id={title} />}>
        <Badge
          color="error"
          variant="dot"
          overlap="circular"
          invisible={!warning}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <IconButton disabled={!warning} sx={{ bgcolor: 'action.hover' }} size="small" color="error" onClick={onOpen}>
            <LocalHospitalRoundedIcon fontSize="small" />
          </IconButton>
        </Badge>
      </Tooltip>
      <NewDialog label="request" open={open} onClose={onClose} maxWidth="xs">
        <NewDialogTitle title={title} onClose={onClose} />
        {loading ? (
          <Stack rowGap={3} py={2} alignItems="center" justifyContent="center" height={140}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack rowGap={1}>
            {result.map((item, i) => {
              const {
                messages,
                check_messages,
                model: { title, status },
              } = item;
              const ready = !check_messages && status?.id === '1';
              const color = check_messages ? 'primary.main' : colors[status.id]?.color;
              const bgcolor = check_messages ? 'primary.lighter' : colors[status.id]?.bgcolor;
              return (
                <React.Fragment key={i}>
                  <Card sx={{ color, bgcolor }}>
                    <CardHeader
                      title={title}
                      action={
                        <Stack direction="row" justifyContent="flex-end" columnGap={0.5}>
                          {ready ? (
                            <Button sx={{ width: 160 }} onClick={() => onRequest()} size="small" variant="contained">
                              درخواست فعالسازی
                            </Button>
                          ) : (
                            <Chip sx={{ width: 160 }} label={status?.title} />
                          )}
                        </Stack>
                      }
                    />
                  </Card>
                  {check_messages && (
                    <Stack rowGap={0.5}>
                      {check_messages.map(({ text }, i) => (
                        <Fade
                          in
                          key={i}
                          unmountOnExit
                          timeout={{ appear: (i + 1) * 150, enter: (i + 1) * 170, exit: (i + 1) * 190 }}
                        >
                          <Card key={i}>
                            <CardHeader
                              title={
                                <Typography color="text.secondary" variant="subtitle2" fontSize={14}>
                                  {text}
                                </Typography>
                              }
                              avatar={
                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'warning.lighter' }}>
                                  <PriorityHighRoundedIcon fontSize="small" color="warning" />
                                </Avatar>
                              }
                            />
                          </Card>
                        </Fade>
                      ))}
                    </Stack>
                  )}
                </React.Fragment>
              );
            })}
          </Stack>
        )}
        <NewDialogActions>
          <Button
            autoFocus
            size="large"
            color="primary"
            variant="contained"
            disabled={loading || requesting}
            onClick={onRequest}
            sx={{ minWidth: 80 }}
          >
            درخواست فعالسازی
          </Button>
        </NewDialogActions>
      </NewDialog>
    </>
  );
}
